<template>
  <q-card>
    <q-card-section>
      <div class="text-h6 q-mb-sm">
        {{ $t('pages.activitySelectDateAndTime.titles.date') }}
      </div>
      <activity-date-toggle
        ref="dateToggle"
        :activity-id="activityBooking.activity.id"
        :initial-date="activityBooking.config.selectedActivitySessionDate"
        @select-date="date =>$emit('select-date', date)"
      />
    </q-card-section>
    <q-card-section>
      <div
        ref="timeAndSpaceContainer"
        class="row q-col-gutter-sm"
      >
        <q-inner-loading
          v-if="$_.isEmpty(buttonWrapperClass)"
          showing
        />
        <div
          v-if="!$_.isEmpty(buttonWrapperClass)"
          class="col column"
        >
          <div class="text-h6 q-mb-sm">
            {{ $t('pages.activitySelectDateAndTime.titles.time') }}
          </div>
          <q-select
            v-model="selectedTimeSlotId"
            class="q-mb-md"
            :options="timeSlotOptions"
            map-options
            emit-value
            option-value="id"
            option-label="displayName"
          />
          <div class="row q-col-gutter-sm">
            <div
              v-for="entryTime in activityBooking.entryTimeOptions(activitySessionsAndAvailableEntries, selectedTimeSlotId)"
              :key="entryTime.value"
              :class="buttonWrapperClass"
            >
              <q-btn
                class="full-width"
                :label="entryTime.label"
                size="lg"
                color="primary"
                :outline="!activityBooking.isEntryTimeSelected(entryTime.value)"
                :disable="entryTime.disable"
                @click="$emit('select-entry-time', entryTime.value)"
              />
            </div>
          </div>
        </div>
        <div
          v-if="!$_.isEmpty(buttonWrapperClass) && $register"
          class="col-6"
        >
          <div class="text-h6 q-mb-sm">
            {{ $t('pages.activitySelectDateAndTime.titles.space') }}
          </div>
          <div class="row q-col-gutter-sm">
            <div
              v-for="activitySpace in activityBooking.allActivitySpaces(activitySessionsAndAvailableEntries)"
              :key="activitySpace.id"
              :class="buttonWrapperClass"
            >
              <q-btn
                class="full-width"
                :label="activitySpace.name"
                size="lg"
                color="primary"
                :outline="!activityBooking.isActivitySpaceSelected(activitySpace)"
                :disable="activityBooking.isActivitySpaceDisabled(activitySpace, activitySessionsAndAvailableEntries)"
                @click="$emit('select-activity-space', activitySpace)"
              />
            </div>
          </div>
        </div>
        <q-resize-observer
          debounce="500"
          @resize="setButtonWrapperClass"
        />
      </div>
    </q-card-section>
  </q-card>
</template>

<script>
import ActivityBooking from 'src/models/activity/ActivityBooking'
import TimeSlot from 'src/models/schedule/TimeSlot'
import ActivityDateToggle from 'src/modules/register/components/activities/ActivityDateToggle'

const MINIMUM_PIXELS_PER_BUTTON = 120

export default {
  name: 'ActivityDateAndTimeCard',
  configuration: ['currentSite', 'timeSlots'],
  components: { ActivityDateToggle },
  props: {
    activityBooking: {
      type: ActivityBooking,
      required: true
    },
    activitySessionsAndAvailableEntries: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: ['select-date', 'select-entry-time', 'select-activity-space'],
  data () {
    return {
      selectedTimeSlotId: null,
      buttonWrapperClass: ''
    }
  },
  computed: {
    timeSlotOptions () {
      return this.activityBooking.timeSlotOptions(this.activitySessionsAndAvailableEntries)
    },
    entryTimeOptions () {
      if (!_.isNil(this.activitySessionsAndAvailableEntries) && !_.isNil(this.selectedTimeSlotId)) {
        return this.activityBooking.entryTimeOptions(this.activitySessionsAndAvailableEntries, this.selectedTimeSlotId)
      } else {
        return []
      }
    }
  },
  watch: {
    timeSlotOptions: {
      handler () {
        if (!_.isNil(this.activityBooking.siteEntryTime)) {
          this.selectedTimeSlotId = TimeSlot.timeSlotForDateTime(this.activityBooking.siteEntryTime).id
        } else if (!_.isEmpty(this.timeSlotOptions)) {
          this.selectedTimeSlotId = _.first(this.timeSlotOptions).id
        }
      },
      immediate: true
    }
  },
  methods: {
    setButtonWrapperClass () {
      const timeAndSpaceWidth = this.$refs.timeAndSpaceContainer?.offsetWidth

      if (timeAndSpaceWidth <= 0) {
        this.buttonWrapperClass = ''
        return
      }

      const totalButtonsPerRow = Math.floor(timeAndSpaceWidth / MINIMUM_PIXELS_PER_BUTTON)

      if (totalButtonsPerRow <= 3) {
        if (this.$register) {
          // We're displaying times beside spaces, so we only have half the
          // width of the timeAndSpaceContainer.
          this.buttonWrapperClass = 'col-12'
        } else {
          // We're not displaying spaces, so we have the full width of the
          // timeAndSpaceContainer.
          this.buttonWrapperClass = 'col-6'
        }

        return
      }

      const colCount = Math.floor(12 / totalButtonsPerRow)

      if (this.$register) {
        this.buttonWrapperClass = `col-${colCount * 2}`
      } else {
        this.buttonWrapperClass = `col-${colCount}`
      }
    }
  }
}
</script>
